import { getCanonicalDeviceStatus, normalizeCanonicalStatus } from "./deviceStatus";

const toTs = (value) => {
  if (!value) return 0;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : 0;
  }
  if (typeof value === "number") return value < 1e12 ? value * 1000 : value;
  if (typeof value === "object") {
    if (value.$date) return toTs(value.$date);
    if (value.$numberLong) return toTs(Number(value.$numberLong));
    if (value.seconds) return toTs(Number(value.seconds));
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const normalizeDeviceListStatus = (status) => {
  const key = String(status || "").trim().toLowerCase();
  if (key === "moving") return "running";
  if (key === "idling") return "idle";
  if (key === "parked") return "stopped";
  if (
    key === "inactive" ||
    key === "offline" ||
    key === "disconnect" ||
    key === "disconnected" ||
    key === "not_reporting" ||
    key === "no_reporting"
  ) {
    return "inactive";
  }
  if (key === "nodata" || key === "no data" || key === "unknown") return "no_data";
  return key || "no_data";
};

const hasRealtimeStatusSignal = (statusState = {}, liveState = {}, mergedLiveData = {}) => {
  const sources = [statusState, liveState, mergedLiveData];
  return sources.some((source) => {
    if (!source || typeof source !== "object") return false;
    return (
      source.accStatus !== undefined ||
      source.ignition !== undefined ||
      source.vehicleStatus !== undefined ||
      source.status !== undefined ||
      source.lastUpdate !== undefined ||
      source.timestamp !== undefined ||
      source.serverTimestamp !== undefined
    );
  });
};

export const resolveDeviceListLikeStatus = (device = {}, socketData = {}, liveOverrides = {}) => {
  const liveData = device?.liveData || {};
  const currentLocation = device?.currentLocation || {};
  const mergedLiveData = { ...currentLocation, ...liveData, ...(liveOverrides || {}), ...(socketData || {}) };

  const statusState = socketData || {};
  const liveState = liveOverrides || {};

  const canonicalSocket = {
    speed: Math.max(0, Number(mergedLiveData?.speed ?? device?.speed) || 0),
    accStatus: mergedLiveData?.accStatus ?? device?.accStatus,
    ignition: mergedLiveData?.ignition ?? device?.ignition,
    vehicleStatus: mergedLiveData?.vehicleStatus ?? mergedLiveData?.status ?? device?.vehicleStatus ?? device?.status,
    movementState: mergedLiveData?.movementState ?? device?.movementState,
    timestamp:
      mergedLiveData?.serverTimestamp ??
      mergedLiveData?.timestamp ??
      mergedLiveData?.lastUpdate ??
      device?.lastUpdate,
  };

  const serverReportedStatus = normalizeDeviceListStatus(
    device?.vehicleStatus ??
      device?.status ??
      mergedLiveData?.vehicleStatus ??
      mergedLiveData?.status ??
      statusState?.vehicleStatus ??
      statusState?.status
  );

  const derivedStatus = normalizeDeviceListStatus(getCanonicalDeviceStatus(device, canonicalSocket));
  const realtimeStatusActive = hasRealtimeStatusSignal(statusState, liveState, mergedLiveData);
  let status =
    (realtimeStatusActive ? derivedStatus : null) ||
    derivedStatus ||
    serverReportedStatus;

  const hasTelemetryTime =
    !!toTs(statusState?.lastUpdate) ||
    !!toTs(mergedLiveData?.serverTimestamp) ||
    !!toTs(mergedLiveData?.timestamp) ||
    !!toTs(mergedLiveData?.lastUpdate) ||
    !!toTs(device?.lastDataReceivedAt) ||
    !!toTs(device?.serverReceivedAt);

  const hasCoordinates =
    Number.isFinite(Number(mergedLiveData?.latitude)) ||
    Number.isFinite(Number(mergedLiveData?.lat)) ||
    Number.isFinite(Number(mergedLiveData?.longitude)) ||
    Number.isFinite(Number(mergedLiveData?.lng)) ||
    Number.isFinite(Number(device?.latitude)) ||
    Number.isFinite(Number(device?.longitude));

  const hasNoData = !hasTelemetryTime && !hasCoordinates;

  if (hasNoData && (!serverReportedStatus || normalizeCanonicalStatus(serverReportedStatus) === "no_data")) {
    status = "no_data";
  }

  return normalizeDeviceListStatus(status);
};
